import type { ArtifactType } from '@/models'
import { useArtifactCacheStore } from '@/stores/artifactCacheStore'
import { useLinkCacheStore } from '@/stores/linkCacheStore'
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

/**
 * Information about a linked artifact type with count
 */
export interface LinkedTypeInfo {
  artifactType: ArtifactType
  linkCount: number
}

/**
 * Composable to compute linked artifact types for a given artifact.
 *
 * @param artifactIdSource - MaybeRefOrGetter that resolves to the artifact ID (or undefined)
 * @returns Object with computed incoming and outgoing linked types
 */
export function useLinkedTypes(artifactIdSource: MaybeRefOrGetter<number | undefined>): {
  incomingTypes: ComputedRef<LinkedTypeInfo[]>
  outgoingTypes: ComputedRef<LinkedTypeInfo[]>
} {
  const artifactCacheStore = useArtifactCacheStore()
  const linkCacheStore = useLinkCacheStore()

  /**
   * Group artifact types by ID and count occurrences
   */
  const groupByTypeWithCount = (artifactTypes: ArtifactType[]): LinkedTypeInfo[] =>
    Object.values(
      artifactTypes.reduce<Record<number, LinkedTypeInfo>>((acc, artifactType) => {
        const existing = acc[artifactType.id]
        acc[artifactType.id] = existing
          ? { ...existing, linkCount: existing.linkCount + 1 }
          : { artifactType, linkCount: 1 }
        return acc
      }, {}),
    )

  const incomingTypes = computed<LinkedTypeInfo[]>(() => {
    const artifactId = toValue(artifactIdSource)
    if (artifactId === undefined) return []

    // Incoming: links where this artifact is the target (artifactBId)
    const sourceArtifactTypes = (linkCacheStore.linksWithB.get(artifactId) ?? [])
      .map((link) => artifactCacheStore.artifactsById.get(link.artifactAId))
      .filter((artifact): artifact is NonNullable<typeof artifact> => artifact !== undefined)
      .map((artifact) => artifact.artifactType)

    return groupByTypeWithCount(sourceArtifactTypes)
  })

  const outgoingTypes = computed<LinkedTypeInfo[]>(() => {
    const artifactId = toValue(artifactIdSource)
    if (artifactId === undefined) return []

    // Outgoing: links where this artifact is the source (artifactAId)
    const targetArtifactTypes = (linkCacheStore.linksWithA.get(artifactId) ?? [])
      .map((link) => artifactCacheStore.artifactsById.get(link.artifactBId))
      .filter((artifact): artifact is NonNullable<typeof artifact> => artifact !== undefined)
      .map((artifact) => artifact.artifactType)

    return groupByTypeWithCount(targetArtifactTypes)
  })

  return {
    incomingTypes,
    outgoingTypes,
  }
}
